import React, {CSSProperties} from 'react';

import Big from 'big.js';
import {BaseMatchedTransaction, GlTransaction, InvoiceTransaction, MatchingResultType, TxType} from "../../../model/MatchedDocument";
import {jsonMapper} from "../../../utils/DataParser";
import {creditTableRowStyle, debitTableRowStyle, inconsistentTableRowStyle, payAttentionTableRowStyle} from "../../theme";
import MyMaterialTable from "../../../components/mtable/MyMaterialTable";
import {Column} from "material-table";
import StopUpdateComponent from "../../../components/StopUpdateComponent";
import {Button} from "@mui/material";
import {transactionOverrideStore} from "../../../store/TransactionOverride";
import ReceiptIcon from '@mui/icons-material/Receipt';
import DeleteIcon from "@mui/icons-material/Delete";
import OverrideInvoiceModal from "./OverrideInvoiceModal";
import {notificationStore} from "../../../store/NotificationStore";
import {BankAccountCode} from "../../../model/BankAccountCode";
import {BaseInvoiceListResponse} from "../../../model/invoice/BaseInvoiceListResponse";


interface IProps {
    bank: BankAccountCode,
    showActions: boolean,
    selectionListener?: ((type: MatchingResultType, selection: BaseMatchedTransaction[]) => void),
    showHiddenColumns: boolean,
    transactions: BaseMatchedTransaction[],
    itemWrapper: (description: string, table: JSX.Element, type: MatchingResultType) => JSX.Element
}

interface IState {
    transactions: Array<BaseMatchedTransaction & TxOverridden>,
    transactionToOverride?: BaseMatchedTransaction
}

interface TxOverridden {
    overridden?: boolean,
    originalTx?: BaseMatchedTransaction
}

export default class TransactionTablesComponent extends React.Component<IProps, IState> {


    constructor(props: Readonly<IProps> | IProps) {
        super(props);
        this.state = {
            transactions: this.getTransactionsWithOverride(props.transactions)
        }
    }

    private getTransactionsWithOverride(transactions: BaseMatchedTransaction[]) {
        return transactions.map(it => {
            if (it.type !== MatchingResultType.WAS_SENT) {
                const override = transactionOverrideStore.getTransaction(this.props.bank.iban, it.tx.txId);
                if (override) {
                    return {...override, overridden: true, originalTx: it}
                }
            }
            return it;
        });
    }

    public render() {
        const tableComponents = this.createTableComponents()
        const typeToTxs = tableComponents
                .map(it => {
                    return {type: it.type, txs: this.getMatchedTransactions(this.state.transactions, it.type)}
                })
                .filter(it => it.txs.length > 0);
        return <>
            {this.state.transactionToOverride && <OverrideInvoiceModal itemToOverride={this.state.transactionToOverride}
                                                                       onClose={this.onInvoiceOverrideClose}
                                                                       onOverride={this.onTransactionOverride}
            />}
            {typeToTxs.map(typeToTx => {
                const component = tableComponents.find(it => it.type === typeToTx.type)!!;
                return this.props.itemWrapper(component.desc, component.component(typeToTx.type, typeToTx.txs), typeToTx.type)
            })}
        </>
    }

    private onInvoiceOverrideClose = () => {
        this.setState({
            transactionToOverride: undefined
        })
    }

    private onTransactionOverride = (originalTx: BaseMatchedTransaction, invoices: BaseInvoiceListResponse[]) => {
        let multiPayments = Object.create(null);
        invoices.forEach(it => {
            multiPayments[it.id] = it.totalSum;
        })
        const override: InvoiceTransaction = {
            tx: originalTx.tx,
            paymentMethod: this.props.bank.iban,
            type: originalTx.tx.txType === TxType.CRDT ? MatchingResultType.SALES_INVOICE : MatchingResultType.PURCHASE_INVOICE,
            invoiceId: invoices.map(it => it.id),
            multiPayments: multiPayments,
            invoiceData: JSON.stringify(invoices),
        }
        transactionOverrideStore.setTransaction(this.props.bank.iban, override)
        const message = "Transaction was moved to " + (override.type === MatchingResultType.SALES_INVOICE ? "sales invoice" : "purchase invoice") + " section";
        notificationStore.addNotification(message, {variant: "success"})
        this.setState({transactions: this.getTransactionsWithOverride(this.props.transactions)})
        this.onInvoiceOverrideClose();
    }

    private clearOverride = (tx: BaseMatchedTransaction) => {
        transactionOverrideStore.removeTransaction(this.props.bank.iban, tx)
        const message = "Transaction returned to default state ";
        notificationStore.addNotification(message, {variant: "success"})
        this.setState({transactions: this.getTransactionsWithOverride(this.props.transactions)})
    }

    private createTableComponents() {
        const multipleInvoiceMatchedStyle = (row: InvoiceTransaction) => row.multipleInvoiceMatchedInconsistency ? inconsistentTableRowStyle : undefined;
        const changedManuallyStyle = (row: any) => row.sent.changedManually ? inconsistentTableRowStyle : undefined;

        const payAttentionToTxStyle = (row: GlTransaction) => (row.accountCodeCredit === "2010" || row.accountCodeDebit === "2010") ? payAttentionTableRowStyle : undefined;

        return [
            {
                desc: "Matched by rules GL transactions",
                type: MatchingResultType.GL_RULE,
                component: this.createTransactionsTable(this.getGlTxsColumns(), ['selection', 'override'], payAttentionToTxStyle),
            },
            {
                desc: "Sales invoices",
                type: MatchingResultType.SALES_INVOICE,
                component: this.createTransactionsTable(this.getInvoiceColumns(), ['selection', 'override'], multipleInvoiceMatchedStyle),
            },
            {
                desc: "Purchase invoices",
                type: MatchingResultType.PURCHASE_INVOICE,
                component: this.createTransactionsTable(this.getInvoiceColumns(), ['selection', 'override'], multipleInvoiceMatchedStyle),
            },
            {
                desc: "Multiple invoices matched",
                type: MatchingResultType.MULTI_MATCH_INVOICE,
                component: this.createTransactionsTable(this.getInvoiceColumns(), ['override']),
            },
            {
                desc: "Missing invoices",
                type: MatchingResultType.INVOICE_MISSING,
                component: this.createTransactionsTable(this.getInvoiceColumns()),
            },
            {
                desc: "Not matched transactions",
                type: MatchingResultType.NOT_MATCHED,
                component: this.createTransactionsTable(this.getNotMatchedColumns()),
            },
            {
                desc: "Transactions sent to merit",
                type: MatchingResultType.WAS_SENT,
                component: this.createTransactionsTable(this.getSentToMeritTxsColumns(), [], changedManuallyStyle),
            }
        ];
    }

    private createTransactionsTable(rawColumns: Column<BaseMatchedTransaction>[], params: Array<'selection' | 'override'> = ['selection', 'override'], additionalRowStyle?: (row: any) => CSSProperties | undefined) {
        const baseRowStyle = ((row: BaseMatchedTransaction) => {
            if (additionalRowStyle) {
                const funStyle = additionalRowStyle(row);
                if (funStyle) {
                    return funStyle;
                }
            }
            return row.tx.txType === TxType.CRDT ? creditTableRowStyle : debitTableRowStyle;
        });

        const debitCreditRowClasses = ((row: any) => {
            const name = row.name;
            if (name === "Total") {
                return payAttentionTableRowStyle
            }
            return name === "Credit" ? creditTableRowStyle : debitTableRowStyle;
        });

        return (type: MatchingResultType, data: BaseMatchedTransaction[]) => {
            const columns: Column<BaseMatchedTransaction & TxOverridden>[] = Array.from(rawColumns);
            if (this.props.showActions && params.includes('override')) {
                columns.push({
                    title: 'Actions',
                    align: "left",
                    render: row => {
                        return <>
                            <Button variant="contained" color="primary" startIcon={<ReceiptIcon/>} onClick={() => {
                                this.setState({
                                    transactionToOverride: row.overridden ? row.originalTx : row
                                })
                            }
                            }>
                                Link
                            </Button>
                            {row.overridden &&
                                    <Button variant="contained" color="secondary" startIcon={<DeleteIcon/>} onClick={() => {
                                        this.clearOverride(row)
                                    }}>
                                        Reset
                                    </Button>
                            }
                        </>
                    }
                })
            }


            return <StopUpdateComponent propsToCheck={[this.state.transactions, this.props.showHiddenColumns]}>
                <div style={{width: "100%"}}>
                    <MyMaterialTable
                            columns={columns}
                            data={data.map((it, index) => {
                                return {...it, index: index + 1};
                            })
                            }

                            onSelectionChange={(rows) => {
                                if (this.props.selectionListener) {
                                    this.props.selectionListener(type, rows)
                                }
                            }}

                            options={{
                                selection: params.includes('selection') && this.props.selectionListener !== undefined,
                                rowStyle: baseRowStyle,
                                toolbar: false,
                                search: false,
                                sorting: true,
                                paging: false,
                                draggable: false,
                                tableLayout: "auto",
                            }}
                    />
                    <hr/>
                    <MyMaterialTable
                            columns={TransactionTablesComponent.getDebitCreditColumns()}
                            data={TransactionTablesComponent.calcDebitCreditData(data)}

                            options={{
                                rowStyle: debitCreditRowClasses,
                                header: false,
                                toolbar: false,
                                search: false,
                                sorting: false,
                                paging: false,
                                draggable: false,
                            }}
                    />
                </div>
            </StopUpdateComponent>
        };
    }

    private getMatchedTransactions(transactions: BaseMatchedTransaction[], type: MatchingResultType) {
        if (transactions) {
            return transactions.filter(it => it.type === type);
        } else {
            return [];
        }
    }

    private getGlTxsColumns() {
        let columns: Column<BaseMatchedTransaction>[] = [
            TransactionTablesComponent.rowNumberColumn(),
            this.txIdColumn(),
            TransactionTablesComponent.txDateColumn(),
            TransactionTablesComponent.txDescriptionColumn(),
            {
                title: 'Party',
                render: (data) => data.tx.txType === TxType.DBIT ? data.tx.creditorName : data.tx.debtorName
            },
            TransactionTablesComponent.subFamilyColumn(),
            TransactionTablesComponent.txTypeColumn(),
            TransactionTablesComponent.amountColumn(),
            {
                field: 'accountCodeDebit',
                title: 'AccCode Debit',
                align: "center"
            },
            {
                field: 'accountCodeCredit',
                title: 'AccCode Credit',
                align: "center"
            }
        ];
        return columns;
    }

    public static txDescriptionColumn(): Column<BaseMatchedTransaction> {
        return {
            field: 'tx.description',
            title: 'Description',
            align: "left",
            width: "40%"
        };
    }

    private static subFamilyColumn(): Column<BaseMatchedTransaction> {
        return {
            field: 'tx.subFamilyId',
            title: 'SubF',
            width: "6%",
            align: "center",
        };
    }

    private static rowNumberColumn(): Column<BaseMatchedTransaction> {
        return {
            title: '#',
            field: 'index',
            width: "4%",
            align: "center",
        };
    }

    private getInvoiceColumns() {
        let columns: Column<BaseMatchedTransaction>[] = [
            TransactionTablesComponent.rowNumberColumn(),
            this.txIdColumn(),
            TransactionTablesComponent.txDateColumn(),
            TransactionTablesComponent.txDescriptionColumn(),
            TransactionTablesComponent.amountColumn(),
            TransactionTablesComponent.txTypeColumn(),
            {
                field: 'party.name',
                title: 'Party Name',
            },
            {
                field: 'paymentMethod',
                title: 'PaymentMethod',
                hidden: !this.props.showHiddenColumns
            },
            {
                field: 'invoiceId',
                title: 'InvoiceId',
                hidden: !this.props.showHiddenColumns
            },
            {
                field: 'invoiceData',
                title: 'Invoice Data',
                hidden: !this.props.showHiddenColumns
            },
            {
                field: 'multiPayments',
                title: 'Invoice to payment',
                hidden: !this.props.showHiddenColumns,
                render: ((_row) => {
                    const row = _row as InvoiceTransaction
                    const multiPayments = row.multiPayments;
                    if (multiPayments) {
                        return jsonMapper.stringify(multiPayments);
                    } else {
                        return "";
                    }
                }),
            }
        ];
        return columns;
    }

    private getNotMatchedColumns() {
        let columns: Column<BaseMatchedTransaction>[] = [
            TransactionTablesComponent.rowNumberColumn(),
            this.txIdColumn(),
            TransactionTablesComponent.txDateColumn(),
            TransactionTablesComponent.txDescriptionColumn(),
            TransactionTablesComponent.subFamilyColumn(),
            TransactionTablesComponent.amountColumn(),
            TransactionTablesComponent.txTypeColumn(),
            {
                field: 'tx.debtorName',
                title: 'Debitor Name',
            },
            {
                field: 'tx.creditorName',
                title: 'Creditor Name',
            },
        ];
        return columns;
    }

    private getSentToMeritTxsColumns() {
        let columns: Column<BaseMatchedTransaction>[] = [
            TransactionTablesComponent.rowNumberColumn(),
            this.txIdColumn(),
            TransactionTablesComponent.txDateColumn(),
            TransactionTablesComponent.txDescriptionColumn(),
            TransactionTablesComponent.amountColumn(),
            TransactionTablesComponent.txTypeColumn(),
            {
                field: 'sent.type',
                title: 'Sent type',
            },
            {
                field: 'sent.accountCodeDebit',
                title: 'AccCode Debit',
                align: "center",
            },
            {
                field: 'sent.accountCodeCredit',
                title: 'AccCode Credit',
                align: "center",
            },
            {
                field: 'sent.invoiceNumbers',
                title: 'Invoices',
                align: "center",
            },

        ];
        return columns;
    }

    private txIdColumn(): Column<BaseMatchedTransaction> {
        return {
            field: 'tx.txId',
            title: 'TxId',
            hidden: !this.props.showHiddenColumns
        };
    }

    public static txDateColumn(): Column<BaseMatchedTransaction> {
        return {
            field: 'tx.txDate',
            title: 'Date',
            type: "date",
            align: "center"
        };
    }

    public static txTypeColumn(): Column<BaseMatchedTransaction> {
        return {
            field: 'tx.txType',
            title: 'TxType',
            headerStyle: {width: '7%', textAlign: "center"},
            align: "center",
        };
    }

    public static amountColumn(): Column<BaseMatchedTransaction> {
        return {
            title: 'Amount',
            field: "tx.amount",
            type: "numeric",
            render: ((row) => row.tx.amount + " " + row.tx.currency),
        };
    }

    private static getDebitCreditColumns() {
        let columns: Column<{ name: any, value: any }>[] = [
            {
                field: 'name',
                title: "",
                width: "10%",
            },
            {
                field: 'value',
                title: "",
                width: "90%",
            }
        ];
        return columns;
    }

    private static calcDebitCreditData(data: BaseMatchedTransaction[]) {
        const debit = data
                .filter(it => it.tx.txType === TxType.DBIT)
                .map(it => it.tx.amount).reduce((a, b) => a.plus(Big(b)), Big(0))

        const credit = data
                .filter(it => it.tx.txType === TxType.CRDT)
                .map(it => it.tx.amount).reduce((a, b) => a.plus(Big(b)), Big(0))

        return [
            {
                name: "Debit",
                value: debit.toString()
            },
            {
                name: "Credit",
                value: credit.toString()
            },
            {
                name: "Total",
                value: credit.minus(debit).toString()
            }
        ]

    }
}